import { RequestCatalogueGroup } from './request-catalogue';
import { EntitlementKind, EntitlementRequestStatus } from './types';

/**
 * What a request is now, computed when it is read from what enforces access, never stored. A
 * released item request reads its own item: `ready` while the rights reader reports that item,
 * `granted` while the item and its agreement are live but not reported (it starts later, or the
 * sponsor is not activated), `ended` once either has ended or been revoked. A released company
 * request reads the company: `preparing` while the organisation reads it but has no result it can
 * open in the latest release, `ready` once it has, `ended` once the organisation no longer reads it.
 */
export enum EntitlementRequestState {
  REQUESTED = 'requested',
  WITHDRAWN = 'withdrawn',
  DECLINED = 'declined',
  GRANTED = 'granted',
  PREPARING = 'preparing',
  READY = 'ready',
  ENDED = 'ended',
}

/** Why a request was refused. A request grants nothing, so none of these changes access. */
export enum EntitlementRequestErrorCode {
  // 400: a key outside the request catalogue, a target the registry forbids, a company that cannot
  // be requested or granted (missing or private), or a grant of another company than the request's.
  VALIDATION_FAILED = 'VALIDATION_FAILED',
  // 410 over HTTP: not there, or not the caller's to see. 404 is rewritten by the CloudFront in front
  // of the API, so not-found answers are 410 throughout.
  NOT_FOUND = 'NOT_FOUND',
  // 409: an open request for the same target; carries that request's state and nothing else.
  ALREADY_REQUESTED = 'ALREADY_REQUESTED',
  // 409: the organisation holds the product or package live, or already reads the company.
  ALREADY_HELD = 'ALREADY_HELD',
  // 409: the request id was used with another body, requester or organisation.
  IDEMPOTENCY_CONFLICT = 'IDEMPOTENCY_CONFLICT',
  // 409: declining, withdrawing or granting a request that is no longer open.
  REQUEST_NOT_OPEN = 'REQUEST_NOT_OPEN',
  // 409: granting a company that has no variant yet. Ops models it first, then grants it.
  COMPANY_NOT_MODELLED = 'COMPANY_NOT_MODELLED',
}

// The JSON of the request routes, dates as ISO strings. A customer route never carries a command,
// agreement, item, rule or binding id; the team routes do, for Ops.

type IsoDate = string;

/**
 * What a customer asks for: a key of the request catalogue, a company by its id, or a company not in
 * the database by its name, with its website when the customer knows it.
 */
export type CreateAccessRequestTarget =
  | { key: string }
  | { companyId: string }
  | { requestedName: string; website?: string };

/** `POST /api/organizations/{organizationId}/access/requests`. */
export interface CreateAccessRequestRequest {
  // The client's id for the request: sending the same request again returns it.
  requestId: string;
  target: CreateAccessRequestTarget;
  // Not taken with a request by name, whose note is the website it gives.
  note?: string;
}

export interface AccessRequestTarget {
  // `coverage:entity` for a company.
  key: string;
  // The catalogue's customer name, or the key of one no longer in it; for a company, its name, or
  // the name the customer gave until a request by name is granted.
  name: string;
  group: RequestCatalogueGroup | null;
  // The requested company: null for an item, and for a request by name until it is granted.
  companyId: string | null;
}

/** A request as a customer route returns it. */
export interface CustomerAccessRequest {
  requestId: string;
  target: AccessRequestTarget;
  note: string | null;
  // Who asked, in an organisation admin's list only; null in a member's own list.
  requestedBy: { userId: number; name: string } | null;
  createdAt: IsoDate;
  decidedAt: IsoDate | null;
  state: EntitlementRequestState;
  // The start of a `granted` request's item, while it is still to come.
  startsAt: IsoDate | null;
  // Set on a declined request: the reason Ops gave.
  declineReason: string | null;
}

/** `GET /api/organizations/{organizationId}/access/requests`. */
export interface CustomerAccessRequestsResponse {
  requests: CustomerAccessRequest[];
}

/** A request as the team's queue shows it, with the command and agreement that fulfilled it. */
export interface EntitlementRequestRecord {
  requestId: string;
  organization: { id: number; name: string };
  requestedBy: { userId: number; name: string } | null;
  target: AccessRequestTarget & {
    kind: EntitlementKind;
    // The name a company was asked for by, when it was not in the database.
    requestedName: string | null;
    // Whether the requested company has a variant; null for an item and an ungranted request by name.
    modelled: boolean | null;
  };
  note: string | null;
  status: EntitlementRequestStatus;
  state: EntitlementRequestState;
  startsAt: IsoDate | null;
  createdAt: IsoDate;
  decidedAt: IsoDate | null;
  decidedBy: { userId: number; name: string } | null;
  decisionReason: string | null;
  fulfilledCommandId: string | null;
  fulfilledAgreementId: string | null;
}

/** `GET /api/internal/entitlement/requests`. */
export interface EntitlementRequestsResponse {
  requests: EntitlementRequestRecord[];
}

/**
 * `POST /api/internal/entitlement/requests/{requestId}/grant-company`: the company to grant, the
 * request's own for a request by id, the one Ops created for a request by name.
 */
export interface GrantCompanyRequestRequest {
  companyId: string;
}

/** `POST /api/internal/entitlement/requests/{requestId}/decline`. */
export interface DeclineEntitlementRequestRequest {
  // Shown to the customer.
  reason: string;
}
